import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import cloudStorageService from '../../../services/cloudStorageService';
import { formatDistanceToNow } from 'date-fns';

const AuditMonitoringPanel = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    action: 'all',
    dateRange: '7days'
  });

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const data = await cloudStorageService?.getAuditLogs(filters);
      setAuditLogs(data);
    } catch (err) {
      console.error('Error loading audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'upload':
        return 'Upload';
      case 'download':
        return 'Download';
      case 'delete':
        return 'Trash2';
      case 'view':
        return 'Eye';
      default:
        return 'FileText';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'upload':
        return 'text-green-600';
      case 'download':
        return 'text-blue-600';
      case 'delete':
        return 'text-red-600';
      case 'view':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Audit Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3">
            <Icon name="Eye" size={20} className="text-blue-600" />
            <div>
              <p className="text-xs text-blue-700 dark:text-blue-300">File Views</p>
              <p className="text-xl font-bold text-blue-900 dark:text-blue-100">1,234</p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <Icon name="Upload" size={20} className="text-green-600" />
            <div>
              <p className="text-xs text-green-700 dark:text-green-300">Uploads</p>
              <p className="text-xl font-bold text-green-900 dark:text-green-100">456</p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <Icon name="Download" size={20} className="text-amber-600" />
            <div>
              <p className="text-xs text-amber-700 dark:text-amber-300">Downloads</p>
              <p className="text-xl font-bold text-amber-900 dark:text-amber-100">789</p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-3">
            <Icon name="Trash2" size={20} className="text-red-600" />
            <div>
              <p className="text-xs text-red-700 dark:text-red-300">Deletions</p>
              <p className="text-xl font-bold text-red-900 dark:text-red-100">23</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <select
            value={filters?.action}
            onChange={(e) => setFilters({ ...filters, action: e?.target?.value })}
            className="w-full h-10 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Actions</option>
            <option value="upload">Uploads</option>
            <option value="download">Downloads</option>
            <option value="view">Views</option>
            <option value="delete">Deletions</option>
          </select>
        </div>
        <div className="flex-1">
          <select
            value={filters?.dateRange}
            onChange={(e) => setFilters({ ...filters, dateRange: e?.target?.value })}
            className="w-full h-10 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>
        </div>
        <Button variant="outline" size="sm" iconName="Download">
          Export Logs
        </Button>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  File
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Timestamp
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <Icon name="Loader" size={24} className="animate-spin text-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : auditLogs?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-600 dark:text-gray-400">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                auditLogs?.map(log => (
                  <tr key={log?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon name={getActionIcon(log?.action)} size={16} className={getActionColor(log?.action)} />
                        <span className="text-sm text-gray-900 dark:text-white capitalize">{log?.action}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {log?.userName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {log?.fileName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {log?.clientName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatDistanceToNow(new Date(log?.timestamp), { addSuffix: true })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditMonitoringPanel;